import { useEffect, useRef, useState } from 'react';
import { FaSpinner, FaUndo } from 'react-icons/fa';
import CostRow from './NeCostRow';
import SafetyStatus from './NeSafetyStatus';
import FleetMix, { fleetState } from './NeFleetMix';
import {
  fetchFleetTypes, parseOdCsv, parseTazXml, validateDemand, runCost,
} from '../utils/networkEditor';

/** Custom demand: replace the baked zones, the OD matrix, the vehicle mix, or any
 *  combination, then run a NEW BASELINE from it — it is not staged like the other
 *  edits. Structure is checked here; MEANING (zone coverage, known vehicle types,
 *  shares) is the edit service's, see parseTazXml and run_edit.normalize_fleet.
 *  A newly uploaded matrix resets the multiplier to 1. */

// Served as static files so the browser downloads them rather than rendering
// them, and so they are the SAME bytes the acceptance run used.
const TEMPLATES = '/data/demand-templates';

// Error cap: the panel is 300 px and a 7,000-row file would print 7,000 lines into it.
const MAX_SHOWN_ERRORS = 6;

const int = (v) => Math.round(Number(v) || 0).toLocaleString();

/** One upload: a file input, its template, its format sheet, its errors. */
function UploadBlock({
  label, accept, hint, templateFile, templateLabel,
  fileRef, onFile, chosen, onClear, errors, helpOpen, onToggleHelp, children,
}) {
  return (
    <div className="ne-demand-block">
      <div className="ne-demand-block-head">
        <span className="ne-section-label">{label}</span>
        <button className="ne-link ne-demand-fmt-toggle" onClick={onToggleHelp}>
          {helpOpen ? 'Hide format' : 'Format'}
        </button>
      </div>

      <input ref={fileRef} type="file" accept={accept}
        className="ne-demand-file" onChange={onFile} />

      <div className="ne-demand-hint">
        {hint}
        {' '}
        <a className="ne-link" href={`${TEMPLATES}/${templateFile}`} download>
          {templateLabel}
        </a>
      </div>

      {helpOpen && <div className="ne-demand-fmt">{children}</div>}

      {chosen && (
        <div className="ne-demand-file-ok">
          {chosen}
          <button className="ne-link" onClick={onClear}>Remove</button>
        </div>
      )}

      {errors.length > 0 && (
        <div className="ne-demand-errors">
          <b>That file was not read.</b>
          {errors.slice(0, MAX_SHOWN_ERRORS).map((m) => (
            <div key={m} className="ne-demand-error-row">{m}</div>
          ))}
          {errors.length > MAX_SHOWN_ERRORS && (
            <div className="ne-demand-error-row">
              …and {errors.length - MAX_SHOWN_ERRORS} more.
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function DemandSection({
  cbsa, demandScale, bakedTrips,
  applied, appliedReport, appliedResult, applying, applyError, phase, safetyJob,
  flows, flowsOn, onToggleFlows, flowMode, onFlowMode, flowFade, onFlowFade,
  onApply, onCapacityHelp, onCostHelp, onReport, onDownloadJson, onResetBaseline,
}) {
  // Open when the flow layer is up: Reset remounts this component (see the `key`
  // in NetworkEditorPanel) but the layer survives, and its only control is here.
  const [open, setOpen] = useState(!!flowsOn);
  const [taz, setTaz] = useState(null);          // { tazXml, count, fileName }
  const [tazErrors, setTazErrors] = useState([]);
  const [od, setOd] = useState(null);            // { rows, odFormat, fileName }
  const [parseErrors, setParseErrors] = useState([]);
  const [fleetCatalog, setFleetCatalog] = useState(null);  // GET /fleet/types
  const [fleetRows, setFleetRows] = useState([]);          // [{ id, pct }]
  // One slot: the two format sheets share a column, so opening one closes the other.
  const [help, setHelp] = useState(null);        // 'taz' | 'od' | null
  const [report, setReport] = useState(null);
  const [checking, setChecking] = useState(false);
  const [checkError, setCheckError] = useState(null);   // {unreachable, message}
  // Follows the current baseline's multiplier until the user takes it over.
  const [scaleField, setScaleField] = useState(applied?.scale || demandScale || 1);
  const [scaleTouched, setScaleTouched] = useState(false);
  const tazRef = useRef(null);
  const odRef = useRef(null);
  // Guards against an out-of-order answer overwriting a newer one: the requests
  // are not guaranteed to come back in the order they went out.
  const reqRef = useRef(0);

  const hasTaz = !!taz?.tazXml;
  const hasOd = !!od?.rows?.length;
  const fleet = fleetState(fleetRows, fleetCatalog?.default);
  const hasFleet = fleet.custom;
  const fileErrors = parseErrors.length + tazErrors.length + fleet.errors.length;
  // A file that failed to read is never "just the other half".
  const usable = !fileErrors && (hasOd || hasFleet);
  // Zones describe no trips on their own; the service says so too, this is the earlier message.
  const tazNeedsOd = hasTaz && !hasOd;

  const scaleNum = Number(scaleField);
  const scaleOk = Number.isFinite(scaleNum) && scaleNum >= 1;
  const scale = scaleOk ? scaleNum : 1;

  // The multiplier the CURRENT baseline runs at. Moving away from it is what
  // makes the multiplier on its own something to apply.
  const baseScale = applied?.scale || demandScale || 1;
  const scaleChanged = scaleOk && scaleNum !== baseScale;
  // The service accepts a payload carrying only `scale`, so the field is live with
  // nothing uploaded.
  const scaleEditable = true;
  // Is there anything to apply? Either an upload, or a multiplier the user has
  // moved off the baseline's own.
  const canSubmit = !fileErrors && (usable || scaleChanged);

  // Keys the check is debounced on. Strings rather than the objects themselves:
  // buildDemand closes over exactly what these identify, and depending on the
  // objects would re-check on every render that rebuilt them.
  const odKey = od ? `${od.fileName}:${od.rows.length}:${od.odFormat}` : '';
  const tazKey = taz ? `${taz.fileName}:${taz.count}` : '';
  const fleetKey = hasFleet ? JSON.stringify(fleet.shares) : '';

  // The key of the demand that was SUBMITTED, compared with what the inputs describe
  // now: same key = the price on screen is the price of what is on screen. Uses the same
  // three strings the validation is debounced on, plus the multiplier — what buildDemand reads.
  const specKey = `${odKey}|${tazKey}|${fleetKey}|${scale}`;
  const submittedKey = useRef(null);
  const [ranKey, setRanKey] = useState(null);
  // `applied` changes only when a run succeeded (set after the await), so a failed apply
  // leaves no measurement.
  useEffect(() => { if (applied) setRanKey(submittedKey.current); }, [applied]);
  const costIsCurrent = ranKey != null && ranKey === specKey;

  // Stays open through a run: the second half outlives the click by ~30 s.
  const busy = !!phase;
  useEffect(() => { if (busy) setOpen(true); }, [busy]);

  // The metro's types and its own mix; the editor starts on that mix.
  useEffect(() => {
    let live = true;
    setFleetCatalog(null);
    fetchFleetTypes(cbsa).then((cat) => {
      if (!live || !cat) return;
      setFleetCatalog(cat);
      setFleetRows(Object.entries(cat.default || {}).map(([id, pct]) => ({ id, pct: String(pct) })));
    });
    return () => { live = false; };
  }, [cbsa]);

  // Follow the current baseline's multiplier while the user has not overridden
  // it. Gated on !hasOd so removing the file walks the default back on its own;
  // the upload direction is set in onOdFile, which is where hasOd flips.
  useEffect(() => {
    if (!scaleTouched && !hasOd) setScaleField(baseScale);
  }, [baseScale, scaleTouched, hasOd]);

  // A new baseline resets ownership of the field: once applied at x3 the field showing 3
  // is the baseline, not a pending edit, so Apply greys out again.
  const appliedScale = applied?.scale;
  useEffect(() => {
    if (appliedScale != null) {
      setScaleTouched(false);
      setScaleField(appliedScale);
    }
  }, [appliedScale]);

  // Each half is omitted unless set. The vehicle mix is sent only when it differs from
  // the metro's own, so an untouched editor does not rerun the baseline as "custom".
  const buildDemand = () => ({
    ...(hasOd ? { od: od.rows, od_format: od.odFormat } : {}),
    ...(hasTaz ? { taz_xml: taz.tazXml } : {}),
    ...(hasFleet ? { fleet: { shares: fleet.shares } } : {}),
    scale,
  });

  // One reader for all three uploads: read the text, hand it to that file's
  // parser, drop whatever report card the previous inputs had earned.
  const readFile = (e, parse, apply, setErrors) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setReport(null);
    setCheckError(null);
    file.text().then((text) => {
      const parsed = parse(text);
      setErrors(parsed.errors);
      apply(parsed.ok ? { ...parsed, fileName: file.name } : null, parsed);
    }).catch(() => {
      setErrors(['That file could not be read.']);
      apply(null, null);
    });
  };

  const onTazFile = (e) => readFile(e, parseTazXml,
    (v) => setTaz(v && { tazXml: v.tazXml, count: v.count, fileName: v.fileName }),
    setTazErrors);

  const onOdFile = (e) => readFile(e, parseOdCsv, (v, parsed) => {
    setOd(v && { rows: v.rows, odFormat: v.odFormat, fileName: v.fileName });
    // An uploaded matrix is somebody's real trip counts, so it starts at x1
    // rather than inheriting whatever multiplier the current baseline runs at.
    if (parsed?.ok && !scaleTouched) setScaleField(1);
  }, setParseErrors);

  const clear = (setValue, setErrors, ref) => () => {
    setValue(null);
    setErrors([]);
    setReport(null);
    setCheckError(null);
    if (ref.current) ref.current.value = '';
  };

  useEffect(() => {
    if (!open || !canSubmit || !scaleOk) {
      // Abort in flight, don't just skip: leaving `checking` set would spin forever when the
      // user removes the file.
      reqRef.current++;
      setChecking(false);
      return undefined;
    }
    const token = ++reqRef.current;
    setChecking(true);
    setCheckError(null);
    const timer = setTimeout(() => {
      validateDemand(buildDemand(), cbsa)
        .then((rep) => {
          if (token !== reqRef.current) return;
          setReport(rep);
          setChecking(false);
        })
        .catch((err) => {
          if (token !== reqRef.current) return;
          setReport(null);
          setCheckError({
            unreachable: !!err.unreachable,
            message: err.message || 'Could not check this demand.',
          });
          setChecking(false);
        });
    }, 350);
    return () => clearTimeout(timer);
  }, [open, canSubmit, scaleOk, odKey, tazKey, fleetKey, scale]);

  const dropped = report?.report?.dropped_unknown_taz;
  const droppedPct = dropped?.trips && report?.report?.trips_in
    ? (100 * dropped.trips) / report.report.trips_in : 0;
  const tazReport = report?.report?.taz;
  const outside = tazReport?.outside_network;
  const estimate = report?.report?.estimate;
  const canApply = !!report && report.ok !== false
    && scaleOk && canSubmit && !checking && !applying;

  // The collapsed summary. Each half is present only when the user set it.
  const appliedParts = !applied ? [] : [
    applied.od?.length ? 'OD' : null,
    applied.taz_xml ? 'zones' : null,
    applied.fleet ? 'fleet' : null,
  ].filter(Boolean);
  const appliedKind = appliedParts.length ? `Custom ${appliedParts.join(' + ')}` : null;
  const appliedEffective = appliedReport?.report?.effective_trips;
  // Reads the APPLIED spec's multiplier, not the field, which may already be edited.
  const shownScale = applied ? (applied.scale || 1) : (demandScale || 1);
  const shownTrips = applied && appliedEffective != null
    ? Math.round(appliedEffective / shownScale)
    : bakedTrips;
  const summaryLine = `${appliedKind || 'Baked AM peak'}`
    + (shownTrips != null ? ` — ${int(shownTrips)} trips ×${shownScale}` : ` ×${shownScale}`);

  // The trips the multiplier row is about: what Apply would run while something
  // is staged, otherwise what the current baseline runs.
  const pendingTrips = report?.ok !== false ? report?.report?.effective_trips : null;
  const rowTrips = canSubmit
    ? pendingTrips
    : (applied && appliedEffective != null
      ? appliedEffective
      : (bakedTrips != null ? bakedTrips * shownScale : null));

  const toggleHelp = (which) => () => setHelp((h) => (h === which ? null : which));

  return (
    <>
      <button className="ne-sect" onClick={() => setOpen(!open)}>
        <span className="ne-sect-caret">{open ? '▾' : '▸'}</span>
        <span>Baseline Adjustments</span>
        <span className={`ne-sect-count ne-demand-summary ${applied ? 'custom' : ''}`}>
          {summaryLine}
        </span>
      </button>

      {open && (
        <div className="ne-sect-body ne-demand">
          <div className="ne-demand-scale">
            <span>Multiplier</span>
            <span className="ne-demand-mult">
              ×
              <input type="number" min="1" max="99" step="1"
                value={scaleField}
                title="Trips are simulated this many times over. Traffic and conflict numbers both use it."
                onChange={(e) => { setScaleTouched(true); setScaleField(e.target.value); }} />
            </span>
            <span className="ne-demand-scale-out">
              → <b>{rowTrips != null ? int(rowTrips) : '—'}</b> trips
            </span>
          </div>
          {!scaleOk && (
            <div className="ne-demand-errors">The multiplier must be 1 or more.</div>
          )}
          <div className="ne-demand-hint">
            Both the traffic numbers and the conflict counts run at this
            multiplier. At ×1 the network is barely loaded — raise it to make an
            effect visible. Raise it too far and the demand exceeds what the
            roads can carry: the traffic results still come back, safety and
            signal analysis are skipped and say so.
          </div>

          {onToggleFlows && (
            <>
              <label className="ne-flow-toggle">
                <input type="checkbox" checked={!!flowsOn}
                  onChange={(e) => onToggleFlows(e.target.checked)} />
                <span>Show trip flows</span>
                {flowsOn && flows?.loading && (
                  <span className="ne-flow-note"><FaSpinner className="ne-spin" size={10} /></span>
                )}
                {flowsOn && !flows?.loading && flows?.trips != null && (
                  <span className="ne-flow-note">
                    {int(flows.trips)} trips · {int(flows.pairs)} pairs
                  </span>
                )}
              </label>
              {flowsOn && flows?.error && (
                <div className="ne-demand-warn">{flows.error}</div>
              )}
              {flowsOn && !flows?.error && onFlowMode && (
                <div className="ne-flow-modes" role="group" aria-label="Trip flow display">
                  <button type="button"
                    className={`ne-flow-mode${flowMode !== 'zone' ? ' active' : ''}`}
                    onClick={() => onFlowMode('all')}>All flows</button>
                  <button type="button"
                    className={`ne-flow-mode${flowMode === 'zone' ? ' active' : ''}`}
                    onClick={() => onFlowMode('zone')}>Click a zone</button>
                </div>
              )}
              {flowsOn && !flows?.error && onFlowFade && (
                <div className="ne-slider-row ne-flow-fade">
                  <label htmlFor="ne-flow-fade">Fade small flows</label>
                  <input id="ne-flow-fade" type="range" min="0" max="80" step="5"
                    value={flowFade}
                    onChange={(e) => onFlowFade(Number(e.target.value))} />
                  <span className="ne-slider-val">{flowFade}</span>
                </div>
              )}
              {flowsOn && !flows?.error && (
                <div className="ne-demand-hint ne-flow-hint">
                  {flows?.zoneName
                    ? <>Showing only flows into and out of <b>{flows.zoneName}</b> — click it
                      again, or anywhere empty on the map, to show all of them.</>
                    : flowMode === 'zone'
                      ? <>Circles are zones, sized by the trips they generate. Click one to
                        draw the trips into and out of it; click anywhere empty to clear it
                        again. Road colours fade while this is on.</>
                      : <>Circles are zones, sized by the trips they generate; lines are the
                        trips between them, and they merge as you zoom out. Click a zone to
                        see only its own flows. Road colours fade while this is on.</>}
                </div>
              )}
            </>
          )}

          <UploadBlock
            label="Zones (optional)"
            accept=".xml,text/xml,application/xml"
            hint={<>SUMO <code>&lt;tazs&gt;</code> XML. Without one, the matrix is
              keyed on the baked zones.</>}
            templateFile="taz_template.xml" templateLabel="Download template"
            fileRef={tazRef} onFile={onTazFile}
            chosen={hasTaz && `${taz.fileName} — ${int(taz.count)} zones`}
            onClear={clear(setTaz, setTazErrors, tazRef)}
            errors={tazErrors}
            helpOpen={help === 'taz'} onToggleHelp={toggleHelp('taz')}
          >
            <p>
              One <code>&lt;taz&gt;</code> per zone, inside a <code>&lt;tazs&gt;</code> root:
            </p>
            <pre>{'<tazs>\n  <taz id="downtown" name="Downtown"\n       shape="-76.506,42.434 -76.506,42.446 -76.487,42.446"/>\n</tazs>'}</pre>
            <ul>
              <li><code>id</code> — unique, and what your OD file must use.</li>
              <li><code>shape</code> — <b>lon,lat</b> pairs (WGS84), at least three.
                The ring is closed for you. Projected metres will be rejected.</li>
              <li><code>name</code> — optional, used in reports only.</li>
            </ul>
            <p>
              Up to 2,000 zones, 5 MB. A zone lying outside the modelled network
              is listed back to you before anything runs; one that lies inside but
              contains no road has its trips dropped and reported.
            </p>
          </UploadBlock>

          <UploadBlock
            label="Origin–destination matrix"
            accept=".csv,text/csv"
            hint={<>CSV, three columns: <code>from_taz,to_taz,count</code> or{' '}
              <code>from_h3,to_h3,count</code>.</>}
            templateFile="od_template.csv" templateLabel="Download template"
            fileRef={odRef} onFile={onOdFile}
            chosen={hasOd && `${od.fileName} — ${int(od.rows.length)} pairs (${od.odFormat.toUpperCase()})`}
            onClear={clear(setOd, setParseErrors, odRef)}
            errors={parseErrors}
            helpOpen={help === 'od'} onToggleHelp={toggleHelp('od')}
          >
            <p>One header row, then one row per origin–destination pair:</p>
            <pre>{'from_taz,to_taz,count\ndowntown,cornell,5200\ncornell,downtown,2400'}</pre>
            <ul>
              <li>The header decides the id space and there is no sniffing —
                a mislabelled file is matched against the wrong zones.</li>
              <li><code>from_taz</code>/<code>to_taz</code> must be ids from your
                uploaded zones; with no zone file they are the baked zone numbers.</li>
              <li><code>from_h3</code>/<code>to_h3</code> are H3 cell addresses,
                and cannot be combined with a zone file.</li>
              <li><code>count</code> — whole trips in the modelled hour, before
                the multiplier above.</li>
            </ul>
            <p>
              A pair naming a zone this network has no road for is dropped, counted
              and sampled back to you — not an error.
            </p>
          </UploadBlock>

          <FleetMix
            catalog={fleetCatalog}
            rows={fleetRows}
            onRows={(rows) => { setFleetRows(rows); setReport(null); setCheckError(null); }}
            routed={appliedResult?.summary?.fleet_vehicles}
            onCapacityHelp={onCapacityHelp}
          />

          {tazNeedsOd && (
            <div className="ne-demand-warn">
              Zones on their own describe no trips — upload an OD matrix keyed on
              their ids to use them.
            </div>
          )}

          {checking && (
            <div className="ne-demand-report checking">
              <FaSpinner className="ne-spin" size={11} /> Checking this demand…
            </div>
          )}

          {!checking && checkError && (
            <div className="ne-demand-report bad">
              {checkError.unreachable
                ? 'Simulation service unreachable — this demand cannot be checked or run.'
                : checkError.message}
            </div>
          )}

          {!checking && !checkError && report && (
            <div className={`ne-demand-report ${report.ok === false ? 'bad' : ''}`}>
              {report.ok === false ? (
                <>
                  <b>This demand was rejected.</b>
                  {/* The service's error shape is {row, message}; a bare string
                      is accepted too rather than rendering an empty row. */}
                  {(report.errors || []).slice(0, MAX_SHOWN_ERRORS).map((e, i) => (
                    <div key={`${e?.row ?? i}-${e?.message ?? e}`} className="ne-demand-error-row">
                      {typeof e === 'string'
                        ? e
                        : <>{e?.row != null ? `Row ${e.row}: ` : ''}{e?.message}</>}
                    </div>
                  ))}
                </>
              ) : (
                <>
                  {tazReport && (
                    <div className="ne-demand-report-row">
                      <span>Zones</span>
                      <span>
                        {int(tazReport.zones_in_network)} of {int(tazReport.zones)} in
                        this network
                      </span>
                    </div>
                  )}
                  {outside?.count > 0 && (
                    <div className="ne-demand-warn">
                      {int(outside.count)} zones fall outside the modelled area.
                      {outside.sample?.length > 0 && (
                        <div className="ne-demand-sample">
                          {outside.sample.slice(0, 3).join(', ')}
                          {outside.sample.length > 3 ? ', …' : ''}
                        </div>
                      )}
                    </div>
                  )}
                  <div className="ne-demand-report-row">
                    <span>Matched</span>
                    <span>
                      {int(report.report?.matched?.pairs)} of {int(report.report?.rows_in)} pairs
                      {' · '}{int(report.report?.matched?.trips)} trips
                    </span>
                  </div>
                  {dropped?.trips > 0 && (
                    <div className="ne-demand-warn">
                      {int(dropped.pairs)} pairs dropped — no matching zone in this
                      network ({droppedPct.toFixed(1)}% of trips).
                      {dropped.sample?.length > 0 && (
                        <div className="ne-demand-sample">
                          {dropped.sample.slice(0, 3).join(', ')}
                          {dropped.sample.length > 3 ? ', …' : ''}
                        </div>
                      )}
                    </div>
                  )}
                  {/* Structural count only — which zones own a road is settled when the run reads the
                      network, so this can only go down. */}
                  {tazReport && (
                    <div className="ne-demand-hint">
                      Zone contents are checked when the run starts — a zone that
                      turns out to hold no road is reported then.
                    </div>
                  )}
                  <div className="ne-demand-report-row">
                    <span>Will simulate</span>
                    <span>
                      <b>{int(report.report?.effective_trips)}</b> trips
                    </span>
                  </div>
                </>
              )}
            </div>
          )}

          {applyError && <div className="ne-run-error">{applyError}</div>}

          {/* Estimate comes from the SERVICE's validation report, not a second lookup in the
              frontend table. Headline = whole run; the "… so far" caption is the macro half. */}
          <CostRow
            est={estimate}
            phase={phase}
            chainNote="od2trips → duarouter → metrics."
            cost={!busy && costIsCurrent ? runCost(appliedResult) : null}
            costSoFar={costIsCurrent ? appliedResult?.cost : null}
            hasWork={canSubmit}
            idleText="Estimated cost appears once you change the demand."
            onHelp={() => onCostHelp && onCostHelp(canSubmit ? buildDemand() : applied)} />

          <button className="ne-execute ne-demand-apply"
            onClick={() => {
              // Remembered before the request so the measurement is attributed to the inputs sent.
              submittedKey.current = specKey;
              onApply(buildDemand(), report);
            }}
            disabled={!canApply}
            title={canSubmit ? undefined
              : 'Change the multiplier or the vehicle mix, or upload a matrix, to run a new baseline.'}>
            {applying
              ? <><FaSpinner className="ne-spin" size={13} /> Running new baseline…</>
              : 'Apply & run new baseline'}
          </button>
          {!busy && (
            <div className="ne-demand-hint centred">
              {canSubmit
                ? `This replaces the baseline — the map and the totals above are
                   redrawn from it, and later edits are measured against it.`
                : `Change the multiplier or the vehicle mix, or upload a matrix, to run a new
                   baseline.`}
            </div>
          )}

          {!busy && <SafetyStatus job={safetyJob} result={appliedResult} />}

          {applied && !applying && (onReport || onDownloadJson) && (
            <div className="ne-demand-exits">
              {onReport && (
                <button className="ne-link" onClick={onReport}>Open report</button>
              )}
              {onDownloadJson && (
                <button className="ne-link" onClick={onDownloadJson}>Download JSON</button>
              )}
            </div>
          )}

          {applied && !busy && onResetBaseline && (
            <button className="ne-reset" onClick={onResetBaseline}>
              <FaUndo size={11} /> Reset to the baked demand
            </button>
          )}
        </div>
      )}
    </>
  );
}
